import { ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import type { PDFDocumentLoadingTask, RenderTask } from 'pdfjs-dist';
import { DrawingRevisionDetail } from '../../../../../../../../shared/models/drawing-sheet';
import { PDF_PAGE_RANGES, PdfRenderService } from '../../../../../../../../shared/services/pdf-render.service';
import { DrawingsAnalytics } from '../drawings-analytics';
import { MAX_CANVAS_PIXELS, sharpScale } from '../drawing-set-page-viewer/drawing-set-page-viewer.component';
import { SHEET_ZOOMS } from '../drawing-markers';
import {
  anchorScrollDelta,
  buttonsReleased,
  capturePointer,
  clampZoom,
  containWidth,
  DragTracker,
  GestureReporter,
  isPinchWheel,
  pageFraction,
  pinchDistance,
  releasePointer,
  stepZoom,
  wheelZoomFactor,
} from '../sheet-zoom-pan';
import { applySimilarity, IDENTITY, invertSimilarity, OverlayColourer, PagePoint, revisionName, Similarity, similarityFromPairs } from '../drawing-overlay';

/** The frame keeps this far from the page at 1× (px) */
const FIT_MARGIN = 16;
const SHARP_DEBOUNCE_MS = 250;

type Source = HTMLImageElement | HTMLCanvasElement;

/** Align manually's steps: a point on the older revision (red), the same point on the newer one (blue), twice */
const ALIGN_STEPS = [
  'Step 1 of 4: click a point on {a}', 'Step 2 of 4: click the same point on {b}',
  'Step 3 of 4: click a second point on {a}', 'Step 4 of 4: click the same second point on {b}',
];

/**
 * W67: Compare's Overlay. Both revisions on one canvas at one scale: removed (only in Rev A) red, added (only in Rev B)
 * blue, unchanged dark grey. The page images at 1×; zoomed in, both pages drawn sharp from their PDFs (the set
 * review's path, 16 MP cap, cancelled when the zoom moves on). Zoom and pan are the sheets' (sheet-zoom-pan, the map
 * stack). Align maps two points of Rev B onto Rev A (a similarity), in memory only.
 */
@Component({
  selector: 'app-drawing-compare-overlay',
  templateUrl: './drawing-compare-overlay.component.html',
  styleUrls: ['./drawing-compare-overlay.component.scss'],
  standalone: false,
})
export class DrawingCompareOverlayComponent implements OnChanges, OnDestroy {
  /** Rev A, the older revision */
  @Input() older: DrawingRevisionDetail;
  /** Rev B, the newer revision */
  @Input() newer: DrawingRevisionDetail;
  @ViewChild('frame') frame?: ElementRef<HTMLElement>;
  @ViewChild('view') view?: ElementRef<HTMLCanvasElement>;

  zoom = 1;
  showRemoved = true;
  showAdded = true;
  /** Rev A ↔ Rev B, 0 … 100 */
  balance = 50;
  /** Align in progress: the points clicked so far (A, B, A, B) */
  aligning: PagePoint[] | null = null;
  transformB: Similarity = IDENTITY;
  /** Auto-align (api 93fa28af): the API's confident alignment of Rev B onto this Rev A; applied on open */
  autoTransform: Similarity | null = null;
  /** transformB is the user's own (Align manually) */
  private manual = false;
  /** The page at 1×, CSS px; null until both pages have loaded */
  fitWidth: number | null = null;
  loadError = '';

  private srcA: Source | null = null;
  private srcB: Source | null = null;
  private images: { a: HTMLImageElement; b: HTMLImageElement } | null = null;
  /** The page masks are drawn for this canvas size (the colourer holds them; review I1) */
  private masked = false;
  private readonly colourer = new OverlayColourer();

  private colourFrame: number | null = null;
  private colourSeq = 0;
  private resize: ResizeObserver | null = null;
  /** Review M5: the preview images could not be read; the pages come from their PDFs at every zoom */
  private pdfOnly = false;
  private renderSeq = 0;
  private docs = new Map<string, PDFDocumentLoadingTask>();
  private tasks: RenderTask[] = [];
  private sharpCanvases: HTMLCanvasElement[] = [];
  private readonly drag = new DragTracker();
  private dragPointer: number | null = null;
  private suppressClick = false;
  private readonly touches = new Map<number, PagePoint>();
  private pinchStart: { distance: number; zoom: number } | null = null;
  private readonly zoomChanged = new Subject<number>();
  private readonly sub = this.zoomChanged.pipe(debounceTime(SHARP_DEBOUNCE_MS)).subscribe(zoom => void this.renderSharp(zoom));
  private readonly track = inject(DrawingsAnalytics);
  private readonly zoomReporter = new GestureReporter<'pinch' | 'wheel'>(method => this.track.track('sheet_zoomed', { method }));
  private readonly pdf = inject(PdfRenderService);
  /** "Rev B", or "Draft" for set review's Preview changes */
  readonly revisionName = revisionName;
  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    // Review N1: a failed Worker hands back to the main thread: the masks are drawn again from the pages held
    this.colourer.onWorkerLost = () => this.compose();
  }

  get canZoomIn(): boolean {
    return this.zoom < SHEET_ZOOMS[SHEET_ZOOMS.length - 1];
  }

  get canZoomOut(): boolean {
    return this.zoom > SHEET_ZOOMS[0];
  }

  get pageWidthPx(): number | null {
    return this.fitWidth ? this.fitWidth * this.zoom : null;
  }

  /** "Step 2 of 4: click the same point on Draft" */
  get alignStep(): string {
    const step = this.aligning?.length ?? 0;
    return (ALIGN_STEPS[step] ?? '').replace('{a}', revisionName(this.older)).replace('{b}', revisionName(this.newer));
  }

  /** Aligned by hand (Reset alignment goes back to the automatic one) */
  get aligned(): boolean {
    return this.manual;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.older && !changes.newer) {
      return;
    }
    this.autoTransform = this.autoAlignment();
    this.transformB = this.autoTransform ?? IDENTITY;
    this.manual = false;
    this.aligning = null;
    this.zoom = 1;
    this.releaseSharp();
    this.loadImages();
  }

  ngOnDestroy(): void {
    this.colourer.terminate();
    this.resize?.disconnect();
    if (this.colourFrame !== null) {
      cancelAnimationFrame(this.colourFrame);
    }
    this.zoomReporter.flush();
    this.sub.unsubscribe();
    this.renderSeq++;
    this.cancelRenders();
    this.releaseSharp();
    this.docs.forEach(d => void d.destroy());
    this.docs.clear();
  }

  // --- The pages ---

  private loadImages(): void {
    const urlA = this.older?.preview_url;
    const urlB = this.newer?.preview_url;
    this.masked = false;
    this.fitWidth = null;
    this.pdfOnly = false;
    this.loadError = '';
    if (!urlA || !urlB) {
      void this.pagesFromPdfs();
      return;
    }
    const load = (url: string) => new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      // The pixels are read back, so the storage link must answer with CORS
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
    const seq = ++this.renderSeq;
    Promise.all([load(urlA), load(urlB)]).then(([a, b]) => {
      if (seq !== this.renderSeq) {
        return;
      }
      this.images = { a, b };
      this.srcA = a;
      this.srcB = b;
      this.fit();
      this.watchResize();
      this.cdr.detectChanges();
      this.compose();
    }, () => {
      // Review M5: a preview the storage serves without CORS (or a cached non-CORS copy): the PDFs instead.
      // No cache-busting query: it would break the signed link.
      void this.pagesFromPdfs();
    });
  }

  /** Review M5: both pages from their PDFs (at 1× and every zoom); a clear message when those fail too */
  private async pagesFromPdfs(): Promise<void> {
    this.pdfOnly = true;
    this.images = null;
    const seq = ++this.renderSeq;
    try {
      const frame = this.frame?.nativeElement;
      const width = Math.max(1, (frame?.clientWidth ?? 800) - 2 * FIT_MARGIN);
      const [a, b] = await this.renderPages(seq, width);
      if (seq !== this.renderSeq) {
        release(a);
        release(b);
        return;
      }
      this.releaseSharp();
      this.sharpCanvases = [a, b];
      this.srcA = a;
      this.srcB = b;
      this.fitFrom(a.width, a.height);
      this.watchResize();
      this.cdr.detectChanges();
      this.compose();
    } catch {
      if (seq === this.renderSeq) {
        this.loadError = 'Overlay needs both pages to load.';
        this.cdr.detectChanges();
      }
    }
  }

  /** Review M4: the frame changes size (window, panels): refit and redraw */
  private watchResize(): void {
    const frame = this.frame?.nativeElement;
    if (this.resize || !frame || typeof ResizeObserver === 'undefined') {
      return;
    }
    let last = frame.clientWidth + 'x' + frame.clientHeight;
    this.resize = new ResizeObserver(() => {
      const now = frame.clientWidth + 'x' + frame.clientHeight;
      if (now === last) {
        return;
      }
      last = now;
      const before = this.fitWidth;
      this.fit();
      if (this.fitWidth !== before) {
        this.cdr.detectChanges();
        this.compose();
      }
    });
    this.resize.observe(frame);
  }

  /** 1× fits (contains) Rev A's page in the frame */
  fit(): void {
    const a = this.srcA;
    if (a) {
      this.fitFrom(sourceWidth(a), sourceHeight(a));
    }
  }

  private fitFrom(width: number, height: number): void {
    const frame = this.frame?.nativeElement;
    if (!frame) {
      return;
    }
    this.fitWidth = containWidth(width, height, frame.clientWidth - 2 * FIT_MARGIN, frame.clientHeight - 2 * FIT_MARGIN)
      ?? Math.max(1, frame.clientWidth - 2 * FIT_MARGIN);
  }

  /** Draws both pages at the canvas's size (Rev B through the alignment), reads their pixels, then colours them */
  compose(): void {
    const canvas = this.view?.nativeElement;
    const a = this.srcA;
    const b = this.srcB;
    const cssWidth = this.pageWidthPx;
    if (!canvas || !a || !b || !cssWidth) {
      return;
    }
    const aspect = sourceHeight(a) / sourceWidth(a);
    let width = Math.round(cssWidth * (window.devicePixelRatio || 1));
    // The 1× page from the images needs no more pixels than they have; one canvas stays within the 16 MP cap
    width = Math.min(width, this.zoom > 1 || a instanceof HTMLCanvasElement ? width : Math.max(sourceWidth(a), 1));
    if (width * width * aspect > MAX_CANVAS_PIXELS) {
      width = Math.floor(Math.sqrt(MAX_CANVAS_PIXELS / aspect));
    }
    const height = Math.max(1, Math.round(width * aspect));
    const draw = (source: Source, transform: Similarity | null) => {
      const c = document.createElement('canvas');
      c.width = width;
      c.height = height;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      // Page units: Rev A's width is 1; Rev B is drawn at Rev A's width, then through the alignment
      ctx.setTransform(width, 0, 0, width, 0, 0);
      if (transform) {
        ctx.transform(transform.a, transform.b, -transform.b, transform.a, transform.tx, transform.ty);
      }
      ctx.drawImage(source, 0, 0, 1, sourceHeight(source) / sourceWidth(source));
      const data = ctx.getImageData(0, 0, width, height);
      c.width = 0;
      c.height = 0;
      return data;
    };
    let masks: { a: ImageData; b: ImageData };
    try {
      masks = { a: draw(a, null), b: draw(b, this.transformB) };
    } catch {
      // Review M5: a tainted preview cannot be read back: the PDFs instead (once); never a blank canvas
      this.masked = false;
      if (!this.pdfOnly) {
        void this.pagesFromPdfs();
      } else {
        this.loadError = 'Overlay needs both pages to load.';
        this.cdr.detectChanges();
      }
      return;
    }
    canvas.width = width;
    canvas.height = height;
    this.colourer.setMasks(masks.a, masks.b);
    this.masked = true;
    this.scheduleColour();
  }

  /** Review I1: one colouring per frame at most, the latest options winning */
  private scheduleColour(): void {
    if (this.colourFrame !== null) {
      return;
    }
    const run = () => {
      this.colourFrame = null;
      void this.colour();
    };
    this.colourFrame = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(run) : (setTimeout(run) as unknown as number);
  }

  /** The colours alone (a toggle or the slider): the page masks stay */
  async colour(): Promise<void> {
    const canvas = this.view?.nativeElement;
    if (!this.masked || !canvas) {
      return;
    }
    const seq = ++this.colourSeq;
    const out = await this.colourer.colour({ showRemoved: this.showRemoved, showAdded: this.showAdded, balance: this.balance / 100 });
    if (!out || seq !== this.colourSeq || out.width !== canvas.width || out.height !== canvas.height) {
      return;
    }
    canvas.getContext('2d').putImageData(out, 0, 0);
  }

  setRemoved(on: boolean): void {
    this.showRemoved = on;
    this.track.track('compare_overlay_toggled', { removed: this.showRemoved, added: this.showAdded });
    this.scheduleColour();
  }

  setAdded(on: boolean): void {
    this.showAdded = on;
    this.track.track('compare_overlay_toggled', { removed: this.showRemoved, added: this.showAdded });
    this.scheduleColour();
  }

  /** The slider fires on every pixel of a drag: the colouring follows at most once a frame, one at a time (review I1) */
  setBalance(value: number): void {
    this.balance = Math.min(100, Math.max(0, value));
    this.scheduleColour();
  }

  // --- Align ---

  startAlign(): void {
    this.aligning = [];
  }

  cancelAlign(): void {
    this.aligning = null;
  }

  /** Back to the automatic alignment (or none) */
  resetAlign(): void {
    this.transformB = this.autoTransform ?? IDENTITY;
    this.manual = false;
    this.aligning = null;
    this.compose();
  }

  /** The marks' numbers: 1, 1, 2, 2 (a red one on the older revision, a blue one on the newer) */
  markNumber(index: number): number {
    return Math.floor(index / 2) + 1;
  }

  /** A click while aligning: a point on Rev A, then the same one on Rev B (as drawn now, so back through the alignment) */
  onCanvasClick(event: MouseEvent): void {
    if (this.consumeDrag() || !this.aligning) {
      return;
    }
    const canvas = this.view?.nativeElement;
    if (!canvas) {
      return;
    }
    const box = canvas.getBoundingClientRect();
    const point = { x: (event.clientX - box.left) / box.width, y: (event.clientY - box.top) / box.width };
    const step = this.aligning.length;
    this.aligning = [...this.aligning, step % 2 === 0 ? point : applySimilarity(invertSimilarity(this.transformB), point)];
    if (this.aligning.length < 4) {
      return;
    }
    const [a1, b1, a2, b2] = this.aligning;
    const transform = similarityFromPairs(a1, a2, b1, b2);
    this.aligning = null;
    if (transform) {
      this.transformB = transform;
      this.manual = true;
      this.track.track('compare_aligned');
      this.compose();
    }
  }

  /**
   * The API's alignment of the newer page onto the older one, when it is confident and was measured against this older
   * revision (an issued revision's is against the one it superseded; a compare across several revisions has none)
   */
  private autoAlignment(): Similarity | null {
    const alignment = this.newer?.alignment;
    if (!alignment?.confident || !this.older || this.newer?.similarity?.against_uuid !== this.older.uuid) {
      return null;
    }
    const { a, b, tx, ty } = alignment.transform;
    return { a, b, tx, ty };
  }

  /** Where the Align marks sit: % of the page box */
  markLeft(p: PagePoint): number {
    return p.x * 100;
  }

  markTop(p: PagePoint): number {
    const a = this.srcA;
    return a ? (p.y / (sourceHeight(a) / sourceWidth(a))) * 100 : 0;
  }

  /** Rev B marks are kept in Rev B's own page; drawn where they show now */
  shownMark(p: PagePoint, index: number): PagePoint {
    return index % 2 === 0 ? p : applySimilarity(this.transformB, p);
  }

  // --- Zoom and pan (the sheets' helper and the map stack) ---

  zoomBy(step: 1 | -1): void {
    const frame = this.frame?.nativeElement;
    this.track.track('sheet_zoomed', { method: 'buttons' });
    if (!frame) {
      return;
    }
    const box = frame.getBoundingClientRect();
    this.zoomAt(stepZoom(SHEET_ZOOMS, this.zoom, step), box.left + frame.clientWidth / 2, box.top + frame.clientHeight / 2);
  }

  zoomAt(zoom: number, clientX: number, clientY: number): void {
    const frame = this.frame?.nativeElement;
    const canvas = this.view?.nativeElement;
    const next = clampZoom(zoom, SHEET_ZOOMS[0], SHEET_ZOOMS[SHEET_ZOOMS.length - 1]);
    if (!frame || !canvas || !this.fitWidth || Math.abs(next - this.zoom) < 1e-3) {
      return;
    }
    const before = canvas.getBoundingClientRect();
    const fx = pageFraction(clientX, before.left, before.width);
    const fy = pageFraction(clientY, before.top, before.height);
    this.zoom = next;
    this.cdr.detectChanges();
    const after = canvas.getBoundingClientRect();
    frame.scrollLeft += anchorScrollDelta(fx, clientX, after.left, after.width);
    frame.scrollTop += anchorScrollDelta(fy, clientY, after.top, after.height);
    if (next === 1 && !this.pdfOnly) {
      // Back on the page images
      this.renderSeq++;
      this.cancelRenders();
      this.useImages();
    } else {
      this.zoomChanged.next(next);
    }
  }

  onWheel(event: WheelEvent): void {
    if (!event.ctrlKey && !event.metaKey) {
      return;
    }
    event.preventDefault();
    this.zoomAt(this.zoom * wheelZoomFactor(event.deltaY), event.clientX, event.clientY);
    this.zoomReporter.step(isPinchWheel(event) ? 'pinch' : 'wheel');
  }

  onPointerDown(event: PointerEvent): void {
    if (event.pointerType === 'touch') {
      this.touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this.touches.size === 2) {
        const [a, b] = Array.from(this.touches.values());
        this.pinchStart = { distance: pinchDistance(a, b), zoom: this.zoom };
      }
      return;
    }
    if (event.button !== 0 || this.zoom <= 1) {
      return;
    }
    this.drag.down(event.clientX, event.clientY);
    this.dragPointer = event.pointerId;
  }

  onPointerMove(event: PointerEvent): void {
    if (event.pointerType === 'touch') {
      if (this.touches.has(event.pointerId)) {
        this.touches.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (this.touches.size === 2 && this.pinchStart) {
          const [a, b] = Array.from(this.touches.values());
          this.zoomAt(this.pinchStart.zoom * (pinchDistance(a, b) / this.pinchStart.distance), (a.x + b.x) / 2, (a.y + b.y) / 2);
          this.zoomReporter.step('pinch');
        }
      }
      return;
    }
    if (!this.drag.active) {
      return;
    }
    const frame = this.frame?.nativeElement;
    if (buttonsReleased(event)) {
      this.endPan();
      return;
    }
    const { dx, dy } = this.drag.move(event.clientX, event.clientY);
    if (this.drag.dragging && this.dragPointer !== null) {
      capturePointer(frame, this.dragPointer);
    }
    if (frame && (dx || dy)) {
      frame.scrollLeft += dx;
      frame.scrollTop += dy;
    }
  }

  onPointerUp(event: PointerEvent): void {
    if (event.pointerType === 'touch') {
      this.touches.delete(event.pointerId);
      if (this.touches.size < 2) {
        this.pinchStart = null;
      }
      return;
    }
    releasePointer(this.frame?.nativeElement, event.pointerId);
    this.dragPointer = null;
    if (this.drag.up()) {
      this.suppressClick = true;
      setTimeout(() => (this.suppressClick = false));
    }
  }

  endPan(): void {
    if (this.dragPointer !== null) {
      releasePointer(this.frame?.nativeElement, this.dragPointer);
    }
    this.dragPointer = null;
    this.drag.cancel();
  }

  private consumeDrag(): boolean {
    if (!this.suppressClick) {
      return false;
    }
    this.suppressClick = false;
    return true;
  }

  // --- Sharp, zoomed in ---

  private useImages(): void {
    this.releaseSharp();
    if (this.images) {
      this.srcA = this.images.a;
      this.srcB = this.images.b;
      this.compose();
    }
  }

  /** Both pages from their PDFs at the zoom's size (16 MP cap each); a newer zoom cancels it. On any failure the images stay */
  private async renderSharp(zoom: number): Promise<void> {
    const cssWidth = this.pageWidthPx;
    if (zoom !== this.zoom || (zoom === 1 && !this.pdfOnly) || !cssWidth) {
      return;
    }
    const seq = ++this.renderSeq;
    try {
      const [a, b] = await this.renderPages(seq, cssWidth);
      if (seq !== this.renderSeq || zoom !== this.zoom) {
        release(a);
        release(b);
        return;
      }
      this.releaseSharp();
      this.sharpCanvases = [a, b];
      this.srcA = a;
      this.srcB = b;
      this.compose();
    } catch {
      // A cancelled render, a failed range request or a page pdf.js cannot draw: what is drawn stays
    }
  }

  /** Both pages from their PDFs, `cssWidth` wide on screen (16 MP cap each); a newer render cancels these */
  private async renderPages(seq: number, cssWidth: number): Promise<[HTMLCanvasElement, HTMLCanvasElement]> {
    const urlA = this.older?.file?.original_url;
    const urlB = this.newer?.file?.original_url;
    if (!urlA || !urlB) {
      throw new Error('no PDF');
    }
    this.cancelRenders();
    const one = async (url: string, pageNumber: number): Promise<HTMLCanvasElement> => {
      let task = this.docs.get(url);
      if (!task) {
        task = this.pdf.openDocument(url, PDF_PAGE_RANGES);
        this.docs.set(url, task);
      }
      const doc = await task.promise;
      const page = await doc.getPage(pageNumber);
      if (seq !== this.renderSeq) {
        throw new Error('superseded');
      }
      const base = page.getViewport({ scale: 1 });
      const render = this.pdf.startRender(page, sharpScale(base.width, base.height, cssWidth, window.devicePixelRatio || 1));
      this.tasks.push(render.task);
      return render.done;
    };
    // The page in the uploaded file (a draft set review page has no page file of its own until publish)
    return Promise.all([one(urlA, this.older.file?.page_number ?? this.older.page_number), one(urlB, this.newer.file?.page_number ?? this.newer.page_number)]);
  }

  private cancelRenders(): void {
    this.tasks.forEach(t => t.cancel());
    this.tasks = [];
  }

  private releaseSharp(): void {
    this.sharpCanvases.forEach(release);
    this.sharpCanvases = [];
  }
}

function sourceWidth(s: Source): number {
  return s instanceof HTMLImageElement ? s.naturalWidth || s.width : s.width;
}

function sourceHeight(s: Source): number {
  return s instanceof HTMLImageElement ? s.naturalHeight || s.height : s.height;
}

/** 0 × 0 frees a canvas's backing store at once */
function release(canvas: HTMLCanvasElement): void {
  canvas.width = 0;
  canvas.height = 0;
}
